// ForgotPassword — production reset flow: email → emailed OTP → new password.
import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Mail, Lock, Loader2, Eye, EyeOff, KeyRound } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import Navbar from '../components/Navbar';
import { formatApiErrorDetail } from '../lib/auth';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PASSWORD_RULE = /^(?=.*[A-Za-z])(?=.*\d).{8,}$/;

export default function ForgotPassword() {
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1 = email, 2 = code + new password
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showPw, setShowPw] = useState(false);
  const [notice, setNotice] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const sendCode = async (e) => {
    e.preventDefault();
    setError(''); setBusy(true);
    try {
      const { data } = await axios.post(`${API}/auth/forgot-password`, { email });
      setNotice(data.message || 'If an account exists for this email, password-reset instructions have been sent.');
      setStep(2);
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail));
    } finally { setBusy(false); }
  };

  const resetPassword = async (e) => {
    e.preventDefault();
    setError('');
    if (!PASSWORD_RULE.test(password)) {
      setError('Password must be at least 8 characters with at least one letter and one number.');
      return;
    }
    if (password !== confirm) { setError('Passwords do not match.'); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/auth/reset-password`, { email, code: code.trim(), new_password: password });
      navigate('/signin', { replace: true, state: { resetDone: true } });
    } catch (err) {
      setError(formatApiErrorDetail(err?.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-teal-50 to-blue-50">
      <Navbar />
      <div className="max-w-md mx-auto px-4 pt-24 pb-16">
        <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-6 md:p-8" data-testid="forgot-password-card">
          <h1 className="text-2xl font-bold text-gray-900 mb-1">Reset your password</h1>
          <p className="text-sm text-gray-500 mb-6">
            {step === 1 ? "Enter your email and we'll send a reset code." : 'Enter the 6-digit code from your email and choose a new password.'}
          </p>
          {notice && step === 2 && (
            <div className="mb-4 text-sm text-teal-700 bg-teal-50 border border-teal-200 rounded-lg px-3 py-2" data-testid="forgot-password-notice">{notice}</div>
          )}
          {error && (
            <div className="mb-4 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2" data-testid="forgot-password-error">{error}</div>
          )}
          {step === 1 ? (
            <form onSubmit={sendCode} className="space-y-4">
              <div>
                <Label htmlFor="fp-email" className="text-gray-700">Email</Label>
                <div className="relative mt-1">
                  <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <Input id="fp-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                    className="pl-9" required data-testid="forgot-password-email" />
                </div>
              </div>
              <Button type="submit" disabled={busy || !email.trim()}
                className="w-full bg-teal-600 hover:bg-teal-700 text-white" data-testid="forgot-password-send">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Send Reset Code'}
              </Button>
            </form>
          ) : (
            <form onSubmit={resetPassword} className="space-y-4">
              <div>
                <Label htmlFor="fp-code" className="text-gray-700">Reset Code</Label>
                <div className="relative mt-1">
                  <KeyRound size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <Input id="fp-code" inputMode="numeric" maxLength={6} value={code}
                    onChange={(e) => setCode(e.target.value.replace(/[^0-9]/g, ''))}
                    className="pl-9 tracking-widest" required data-testid="forgot-password-code" />
                </div>
              </div>
              <div>
                <Label htmlFor="fp-pw" className="text-gray-700">New Password</Label>
                <div className="relative mt-1">
                  <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <Input id="fp-pw" type={showPw ? 'text' : 'password'} value={password}
                    onChange={(e) => setPassword(e.target.value)} className="pl-9 pr-10"
                    placeholder="Min 8 chars, letters + numbers" required data-testid="forgot-password-new" />
                  <button type="button" onClick={() => setShowPw((s) => !s)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                    data-testid="forgot-password-toggle-pw" aria-label="Show or hide password">
                    {showPw ? <EyeOff size={16} /> : <Eye size={16} />}
                  </button>
                </div>
              </div>
              <div>
                <Label htmlFor="fp-pw2" className="text-gray-700">Confirm New Password</Label>
                <div className="relative mt-1">
                  <Lock size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                  <Input id="fp-pw2" type={showPw ? 'text' : 'password'} value={confirm}
                    onChange={(e) => setConfirm(e.target.value)} className="pl-9" required
                    data-testid="forgot-password-confirm" />
                </div>
              </div>
              <Button type="submit" disabled={busy || !code || !password || !confirm}
                className="w-full bg-teal-600 hover:bg-teal-700 text-white" data-testid="forgot-password-submit">
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Reset Password'}
              </Button>
              <button type="button" className="w-full text-sm text-gray-500 hover:text-gray-700"
                onClick={() => { setStep(1); setError(''); }} data-testid="forgot-password-back">
                Use a different email
              </button>
            </form>
          )}
          <p className="mt-6 text-center text-sm text-gray-500">
            Remembered it? <Link to="/signin" className="text-teal-700 font-semibold hover:underline" data-testid="forgot-password-signin-link">Sign in</Link>
          </p>
        </div>
      </div>
    </div>
  );
}
